'use client';

import {
  forwardRef,
  ReactNode,
  SelectHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { tv } from 'tailwind-variants';
import { v4 as uuid } from 'uuid';

import '@/styles/globals.v2.css';

import { cloneDeep, isEmpty, isEqual } from 'lodash';
import Image from 'next/image';

import Check from '@/components/svg/Check';
import TextSmall from '@/components/V2/Typography/TextSmall';
import {
  IbmPlexItalic,
  IbmPlexItalicBold,
  IbmPlexItalicMedium,
  IbmPlexItalicSemiBold,
  SweetSansPro,
  SweetSansProBold,
  SweetSansProExtraLight,
  SweetSansProLight,
  SweetSansProMedium,
  SweetSansProSemiBold,
} from '@/config/fonts';
import { useDetectClickOutside, useWindowDimensions } from '@/helpers/hooks';
import useScrollListener from '@/helpers/hooks/useScrollListener';
import cn from '@/helpers/utils/classNames';

import styles from './index.module.css';

export interface SelectProps<T extends Record<string, any>>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  options: Array<T> | null;
  displayKey: keyof T;
  valueKey: keyof T;
  value: T[keyof T];
  defaultOption?: T;
  error?: string;
  message?: string;
  className?: string;
  labelClassName?: string;
  wrapperClassName?: string;
  leftIconClasssName?: string;
  variant?: 'rounded' | 'compact' | 'boxed';
  outline?: 'default' | 'primary';
  onChange?: (value: T[keyof T]) => void;
  validator?: (value: T[keyof T] | string) => boolean;
  onMount?: () => void;
  onClose?: () => void;
  label?: string | ReactNode;
  extraText?: string;
  extraTextClassName?: string;
  ref?: unknown;
  optionsContainerClassName?: string;
  allowElipsis?: boolean;
  leftIcon?: any;
  showOptionTick?: boolean;
  optionClassName?: string;
  placeholderClassName?: string;
}

const selectInputStyle = tv({
  base: `${styles.__select} v2-min-w-[6rem] v2-text-left v2-border-grayscale-10 v2-cursor-pointer v2-text-grayscale-70 v2-rounded focus:v2-outline-none focus:!v2-border-primary focus-visible:!v2-shadow-input-focus-primary v2-font-light v2-outline-transparent v2-py-2 v2-px-2 v2-bg-white v2-pl-[1rem]`,
  variants: {
    variant: {
      boxed: 'v2-border',
      compact: 'v2-rounded-none v2-border-b v2-focus:shadow-none v2-bg-transparent',
      rounded: 'v2-rounded-full v2-border',
    },
    outline: {
      default: '',
      primary: 'focus:v2-border-primary focus:v2-shadow-input-focus-primary',
    },
  },
});
type T = Record<any, any>;

const getNextIndexToMove = (currentIndex: number, options: Array<T> | null): number => {
  const nextOption = options?.[currentIndex + 1];
  const startIndex = currentIndex < 0 ? 0 : currentIndex;

  const disabledOptionsCount =
    nextOption && nextOption['disabled']
      ? options?.slice(startIndex, options.length)?.reduce((acc, curr) => (curr['disabled'] ? acc + 1 : acc), 1)
      : 1;

  return currentIndex + disabledOptionsCount < options!.length ? currentIndex + disabledOptionsCount : currentIndex;
};

const getPrevIndexToMove = (currentIndex: number, options: Array<T> | null): number => {
  const prevOption = options?.[currentIndex - 1];

  const disabledOptionsCount =
    prevOption && prevOption['disabled']
      ? options?.slice(0, currentIndex)?.reduce((acc, curr) => (curr['disabled'] ? acc + 1 : acc), 1)
      : 1;

  return currentIndex - disabledOptionsCount >= 0 ? currentIndex - disabledOptionsCount : currentIndex;
};

const CustomSelect = forwardRef<{ validate: () => Promise<boolean> } & Partial<HTMLSelectElement>, SelectProps<T>>(
  function Select(
    {
      options,
      valueKey,
      displayKey,
      value,
      defaultOption,
      className,
      wrapperClassName,
      variant,
      onMount,
      onChange,
      label,
      name,
      error,
      message,
      outline,
      validator,
      extraText,
      extraTextClassName,
      labelClassName,
      optionsContainerClassName,
      placeholder = 'Select an option',
      allowElipsis,
      leftIcon,
      leftIconClasssName,
      showOptionTick = true,
      onClose,
      optionClassName,
      placeholderClassName,
      disabled,
    },
    ref
  ) {
    const [isOpen, setIsOpen] = useState(false);
    const [data, setData] = useState<{
      isValidationFailed: boolean;
      value: T[keyof T] | string;
    }>({
      isValidationFailed: false,
      value: '',
    });
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [selectedValue, setSelectedValue] = useState<T[keyof T] | string>(value || '');
    const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);
    const [menuPlacement, setMenuPlacement] = useState({ top: 0 });
    const [searchTerm, setSearchTerm] = useState('');

    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
    const optionsContainerRef = useRef<HTMLUListElement | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { width, height } = useWindowDimensions();

    const allOptions = useMemo(() => {
      const clonedOptions = cloneDeep(options);
      const clonedDefaultOption = cloneDeep(defaultOption);

      return Array.isArray(clonedOptions)
        ? !isEmpty(clonedDefaultOption)
          ? [clonedDefaultOption, ...clonedOptions]
          : clonedOptions
        : null;
    }, [options, defaultOption]);

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const calculateDropdownPosition = useCallback((width: number, height: number) => {
      const htmlElement = document.documentElement;
      const zoomExist = htmlElement.getBoundingClientRect().width < 2000;

      let zoom = zoomExist ? 1.25 : 1;

      console.log(zoom);
      if (selectRef?.current && optionsContainerRef?.current) {
        let position = 0;

        const { bottom, top } = selectRef.current.getBoundingClientRect();
        const { height: menuHeight } = optionsContainerRef?.current?.getBoundingClientRect() ?? {};

        const remainingBottomSpace = Math.abs(height - bottom);

        if (remainingBottomSpace < menuHeight && menuHeight > top) {
          const cords = Math.abs(height - menuHeight) / 2;
          position = width < 768 ? cords : cords * zoom;
        } else if (remainingBottomSpace >= menuHeight) {
          position = width < 768 ? bottom : bottom * zoom;
        } else {
          const cords = top - menuHeight;
          position = width < 768 ? cords : cords * zoom;
        }

        setMenuPlacement({
          top: position,
        });
      }
    }, []);

    useScrollListener(() => {
      calculateDropdownPosition(width, height);
    });

    useDetectClickOutside(() => {
      setFocusedIndex(-1);
      setIsOpen(false);
      onClose && onClose();
    }, dropdownRef);

    const handleToggleDropdown = () => {
      if (!disabled) setIsOpen(prev => !prev);
    };

    const handleOptionSelect = (option: T) => {
      setSelectedValue(option[valueKey]);
      onChange && onChange(option[valueKey]);
      setIsOpen(false);
      onClose && onClose();
    };

    const handleMouseOver = (index: number) => {
      if (!isKeyboardNavigating) setFocusedIndex(index);
    };

    const handleMouseMove = () => {
      setIsKeyboardNavigating(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsKeyboardNavigating(true);

      const char = e.key.toLowerCase();

      // Only process alphanumeric characters
      if (/^[a-z0-9]$/i.test(char)) {
        // Add typed character to search term
        setSearchTerm(prev => prev + char);

        // Reset the search term after a short delay
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          setSearchTerm('');
        }, 500); // Reset search after 500ms of inactivity
      }

      if (e.key === 'ArrowDown') {
        setFocusedIndex(prev => {
          const newIndex = getNextIndexToMove(prev, allOptions);
          scrollToOption(newIndex); // Scroll into view

          return newIndex;
        });
      } else if (e.key === 'ArrowUp') {
        setFocusedIndex(prev => {
          const newIndex = getPrevIndexToMove(prev, allOptions);
          scrollToOption(newIndex); // Scroll into view

          return newIndex;
        });
      } else if (e.key === 'Enter' && focusedIndex >= 0) {
        handleOptionSelect(allOptions![focusedIndex]);
      }
    };

    // Scrolls the focused option into view
    const scrollToOption = useCallback(
      (index: number) => {
        const optionElement = optionRefs.current[index];
        if (optionElement) {
          optionElement.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      },
      [optionRefs]
    );

    useEffect(() => {
      if (searchTerm) {
        const matchedIndex =
          allOptions?.findIndex(option => option[displayKey]?.toLowerCase()?.includes(searchTerm)) ?? -1;

        if (matchedIndex !== -1 && optionRefs.current) {
          scrollToOption(matchedIndex);

          if (!allOptions?.[matchedIndex]?.disabled) {
            setFocusedIndex(matchedIndex);
          }
        }
      }
    }, [searchTerm, scrollToOption, allOptions, displayKey]);

    useEffect(() => {
      onMount && onMount();
    }, [onMount]);

    useImperativeHandle(
      ref,
      () => ({
        validate: async () => {
          if (!validator) return false;

          return new Promise<boolean>(resolve =>
            setData(prev => {
              const isValidationFailed = !validator(`${prev.value}`);
              resolve(isValidationFailed);
              return { ...prev, isValidationFailed };
            })
          );
        },
      }),
      [validator]
    );

    const selectId = uuid();
    const hasSelectedValue = selectedValue !== undefined && selectedValue !== null && `${selectedValue}`.trim() !== '';
    const selectedOption = hasSelectedValue ? allOptions?.find(opt => opt[valueKey] === selectedValue) : undefined;
    const selectedLabel = selectedOption?.[displayKey];
    const shouldShowPlaceholder =
      !hasSelectedValue || selectedLabel === undefined || selectedLabel === null || `${selectedLabel}`.trim() === '';

    useLayoutEffect(() => {
      calculateDropdownPosition(width, height);

      if (!isOpen) {
        setFocusedIndex(-1);
      }
    }, [isOpen, calculateDropdownPosition, width, height]);

    useEffect(() => {
      const formattedValue = !value ? '' : value;
      if (data.value !== formattedValue) setData(prev => ({ ...prev, value: formattedValue as T[keyof T] }));
    }, [data, value]);

    useEffect(() => {
      setSelectedValue(value);
    }, [value]);

    const showerror = (!validator && error) || (!!validator && data.isValidationFailed && error);

    return (
      <div
        className={cn(
          SweetSansProExtraLight.variable,
          SweetSansProLight.variable,
          SweetSansPro.variable,
          SweetSansProMedium.variable,
          SweetSansProSemiBold.variable,
          SweetSansProBold.variable,
          IbmPlexItalic.variable,
          IbmPlexItalicBold.variable,
          IbmPlexItalicMedium.variable,
          IbmPlexItalicSemiBold.variable,
          'v2-w-full v2-flex v2-flex-col v2-gap-[6px]',
          wrapperClassName
        )}
      >
        {label && (
          <label
            htmlFor={selectId}
            className={cn('v2-text-sm v2-text-[#344054] v2-font-sweet-sans-pro v2-font-normal block', labelClassName)}
          >
            {label}
          </label>
        )}

        <div className="v2-relative v2-w-full v2-flex" ref={dropdownRef}>
          <div
            className={cn(
              selectInputStyle({ variant: variant || 'rounded', outline }),
              'v2-my-0 v2-font-sweet-sans-pro v2-w-full v2-text-base v2-text-gray-900 v2-pr-7 v2-inline-block v2-whitespace-nowrap v2-text-ellipsis v2-overflow-auto md:v2-overflow-auto sm:v2-overflow-hidden xs:v2-overflow-hidden',
              { 'v2-border !v2-border-red-500 focus:!v2-border-red-500 focus:!v2-shadow-none': showerror },
              { '!v2-cursor-not-allowed': disabled },
              // {
              //   '':
              //     allowElipsis,
              // },
              { '!v2-pl-9': leftIcon },
              `${isOpen ? styles.selectActive : ''}`,
              className
            )}
            onClick={handleToggleDropdown}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            ref={selectRef}
          >
            {leftIcon && (
              <Image
                src={leftIcon}
                alt="Left Icon"
                className={cn('v2-absolute v2-left-3 v2-top-[11px]', leftIconClasssName)}
              />
            )}
            {shouldShowPlaceholder ? (
              <span className={placeholderClassName}>{placeholder || 'Select an option'}</span>
            ) : (
              selectedLabel
            )}
          </div>

          {isOpen && (
            <ul
              className={cn(
                'v2-rounded-[4px] v2-space-y-[2px] v2-bg-white v2-border v2-max-h-60 v2-overflow-y-auto v2-font-sweet-sans-pro v2-leading-5 v2-scrollbar-thin v2-scrollbar v2-scrollbar-thumb-[#888] v2-scrollbar-track-[#f1f1f1] z-[9999999]',
                optionsContainerClassName
              )}
              ref={optionsContainerRef}
              onMouseMove={handleMouseMove}
              style={{
                width: dropdownRef?.current?.clientWidth ?? '100%',
                top: menuPlacement.top,
                position: 'fixed',
              }}
            >
              {allOptions?.map((option, index) => (
                <li
                  ref={el => (optionRefs.current[index] = el)}
                  key={option[valueKey]}
                  onMouseOver={() => {
                    if (!option['disabled']) handleMouseOver(index);
                  }}
                  className={cn(
                    'v2-py-[9px] v2-px-[10px] v2-mx-[6px] v2-rounded-[4px] v2-text-gray-800',
                    {
                      'v2-bg-primary v2-text-white':
                        selectedValue === option[valueKey] && !isEqual(allOptions[focusedIndex], option),
                      'v2-bg-[#C5D1CC] v2-text-gray-800': focusedIndex === index,
                      'v2-text-[#667085] cursor-not-allowed select-none': option['disabled'],
                    },
                    optionClassName
                  )}
                  onClick={() => {
                    if (!option['disabled']) {
                      handleOptionSelect(option);
                    } else {
                      const dropdown = dropdownRef?.current?.firstChild as HTMLDivElement;
                      dropdown?.focus();
                    }
                  }}
                >
                  <div className="v2-flex v2-items-center v2-gap-2">
                    {showOptionTick && selectedValue === option[valueKey] && (
                      <Check stroke={!isEqual(allOptions[focusedIndex], option) ? 'white' : '#182230'} />
                    )}
                    <span className="overflow-hidden overflow-ellipsis whitespace-nowrap">{option[displayKey]}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {showerror && error && (
          <p className="v2-font-sweet-sans-pro v2-text-sm v2-text-red-600 v2-font-light">{error}</p>
        )}
        {message && <TextSmall variant={error ? 'danger' : 'light-gray'}>{message}</TextSmall>}
      </div>
    );
  }
);

export default CustomSelect;
